// Mock data for Pavan Suresh - DevOps Engineer Portfolio

export const personalInfo = {
  name: "Pavan Suresh",
  title: "DevOps Specialist",
  location: "Melbourne, VIC, Australia",
  email: "mpavan.k.15@gmail.com",
  phone: "+61481863789",
  linkedin: "linkedin.com/in/sshprotocol",
  summary: "9+ years with software development and cloud reliability experience. AWS/GCP + containers + CI/CD at scale, leading teams to ship resilient enterprise platforms"
};

export const experience = [
  {
    id: 1,
    company: "JDR Software",
    role: "DevOps Specialist",
    period: "Mar 2024 - Present",
    location: "Melbourne, AU",
    highlights: [
      "Engineered company's first real-time monitoring and alerting platform",
      "Managed automated deployment pipelines for 30+ universities across 120+ environments",
      "Deployed HashiCorp Vault for secrets management with in-memory runtime loading",
      "Built intelligent discovery scripts for unmanaged endpoints",
      "Implemented GraphQL-based performance insights and query optimizations"
    ]
  },
  {
    id: 2,
    company: "Monash University",
    role: "Software Developer",
    period: "Jun 2022 - Apr 2023",
    location: "Melbourne, AU",
    highlights: [
      "Built ML-powered API services for chatbot intent and response accuracy",
      "Improved reporting system accuracy by 40% through backend optimization",
      "Built performance testing tools and managed CI/CD pipelines on GCP/AWS"
    ]
  },
  {
    id: 3,
    company: "NASDAQ",
    role: "Sr. DevOps Specialist / Sr Software Developer",
    period: "May 2019 - Feb 2022",
    location: "Bangalore, IN",
    highlights: [
      "Led Global team as Technical Operation Manager (TOM)",
      "Led Master Search Tool project achieving 50% faster lookup",
      "Developed full-stack internal software approval system",
      "Developed Python API service for automated firewall whitelist management"
    ]
  },
  {
    id: 4,
    company: "IGS",
    role: "DevOps Engineer",
    period: "Jun 2018 - May 2019",
    location: "Bengaluru, IN",
    highlights: [
      "Led development of AD-validator for OTT platform",
      "Automated continuous deployment using Ansible and Jenkins",
      "Set up SOC system with ELK stack for log aggregation and alerting"
    ]
  },
  {
    id: 5,
    company: "NxtGen Infinite Datacenter",
    role: "Cloud Engineer",
    period: "Jun 2017 - Jun 2018",
    location: "Bengaluru, IN",
    highlights: [
      "Automated network configurations using Nvidia Cumulus Linux",
      "Built backend services in Java for configuration validation",
      "Set up CI/CD pipelines in AWS for infrastructure deployments"
    ]
  }
];

export const projects = [
  {
    id: 1,
    title: "Distributed Monitoring Platform",
    problem: "Need for scalable, multi-tenant monitoring solution to track health of diverse endpoints (HTTP, API, DB) across 30+ customer environments with real-time alerting and anomaly detection.",
    solution: "Built distributed monitoring platform using Amazon ECS with isolated containers per customer. Implemented real browser checks using Chromium for complex health validations. Integrated CloudWatch, SNS/SQS for metrics and asynchronous alerting.",
    results: "Achieved 99.9% uptime monitoring reliability. Reduced MTTR by 60% through automated anomaly detection. Successfully scaled to monitor 500+ endpoints across 30+ customers with zero cross-tenant data leakage.",
    timeline: "6 months",
    role: "Lead DevOps Engineer & Architect",
    teamSize: "1",
    technologies: ["Amazon ECS", "AWS CloudWatch", "SNS/SQS", "Uptime Kuma", "Java", "Python", "Bash", "Chromium", "Terraform"],
    flowchart: {
      problem: "Multi-tenant monitoring at scale",
      stack: ["Amazon ECS", "CloudWatch", "Uptime Kuma", "SNS/SQS"],
      implementation: ["Isolated ECS containers per customer", "Real browser health checks", "Automated maintenance windows", "Anomaly detection algorithms"],
      result: "99.9% uptime, 60% faster incident response"
    }
  },
  {
    id: 2,
    title: "STIG-Compliant Ubuntu Infrastructure for Enterprise-Grade Operations",
    problem: "Nasdaq's first Ubuntu Desktop rollout required centralized management, automated security patching, and STIG compliance across entire estate without existing automation framework.",
    solution: "Deployed Canonical Landscape for centralized systems management. Created custom Bash automation scripts for security patching, auditing, and access control. Implemented continuous vulnerability tracking and compliance reporting.",
    results: "Successfully deployed and managed 200+ Ubuntu desktops. Achieved 100% STIG compliance. Reduced security patch deployment time from days to hours. Zero security incidents post-deployment.",
    timeline: "1 year",
    role: "Technical Operations Manager (TOM)",
    teamSize: "5 engineers",
    technologies: ["Ubuntu", "Canonical Landscape", "Bash", "STIG Compliance", "Python", "Terraform"],
    flowchart: {
      problem: "Enterprise Ubuntu deployment with compliance",
      stack: ["Ubuntu Desktop", "Canonical Landscape", "Bash Scripts", "STIG Framework"],
      implementation: ["Centralized management setup", "Automated security patching", "Custom repository management", "Continuous compliance tracking"],
      result: "200+ desktops, 100% STIG compliant, zero incidents"
    }
  },
  {
    id: 7,
    title: "MLOps-Driven Retrieval-Augmented Training Platform",
    problem: "Training content for visual testing and welding inspection was scattered across PDFs, videos, and audio. Learners needed fast, role-aware answers with citations while the business required production-grade MLOps with versioning, evaluation gates, and rollback.",
    solution: "Built a multi-tenant RAG system with automated ingestion for PDFs/videos/audio. Lambda handled lightweight routing and event triggers, while heavy processing ran on EKS workers: Apache Tika extracted text/metadata, LayoutParser detected structure, Camelot/Tabula captured tables, and Tesseract OCR handled scanned pages. FFmpeg + Whisper produced time-stamped transcripts (PyAnnote for speaker tags). SentenceTransformers generated embeddings, FAISS provided fast vector search, and metadata lived in DynamoDB/Postgres for tenant/course/revision filters. Step Functions orchestrated the pipeline, S3 stored raw + versioned artifacts, and CI/CD ran evaluation gates before promoting KB versions. The runtime used an open-source LLM (Llama/Mistral) via LangChain/LlamaIndex to enforce citations, with CloudWatch monitoring and fast rollback to prior KB releases.",
    results: "Top‑3 retrieval precision ~87%, citation coverage ~94%, hallucination rate <2.1%. Quiz scores improved by 26% and time‑to‑competency reduced by 31% during pilot.",
    timeline: "4 months",
    role: "MLOps Engineer / Tech Lead",
    teamSize: "4 engineers",
    technologies: [
      "Python",
      "AWS S3",
      "EventBridge",
      "Step Functions",
      "Lambda",
      "Apache Tika",
      "LayoutParser",
      "Camelot/Tabula",
      "Tesseract OCR",
      "FFmpeg",
      "Whisper",
      "SentenceTransformers",
      "FAISS",
      "LangChain/LlamaIndex",
      "Llama 3 / Mistral",
      "CloudWatch",
      "DynamoDB/Postgres",
      "Terraform"
    ],
    flowchart: {
      problem: "Multi-modal training data with strict accuracy + MLOps needs",
      stack: ["RAG (Open-source LLM)", "FAISS", "S3", "Step Functions", "CloudWatch"],
      implementation: ["Automated ingestion + chunking", "Embeddings + vector search", "Eval gates + KB versioning", "Monitoring + rollback"],
      result: "94% citations, <2.1% hallucinations, 31% faster competency"
    }
  },
  {
    id: 4,
    title: "Software Approval System",
    problem: "Manual software approval process created bottlenecks, security risks, and lack of visibility into software authorization across organization. No integration with security tools.",
    solution: "Developed internal automation platform using Python Flask and ReactJS. Integrated Carbon Black and CrowdStrike APIs for hash verification. Implemented REST APIs, secure data handling, and automated email workflows from endpoint to Infosec.",
    results: "Reduced approval time from 3-5 days to 2-4 hours. Processed 500+ software requests in first quarter. Blocked 15+ malicious software attempts through automated hash verification.",
    timeline: "1.5 years",
    role: "Full-stack DevOps Developer",
    teamSize: "5-8",
    technologies: ["Python Flask", "ReactJS", "AWS", "Carbon Black", "CrowdStrike", "REST APIs","MongoDB"],
    flowchart: {
      problem: "Manual approval causing delays and security gaps",
      stack: ["Python Flask", "ReactJS", "Carbon Black", "CrowdStrike API"],
      implementation: ["Endpoint plugin integration", "Automated hash verification", "Workflow automation", "Email notifications"],
      result: "3-5 days → 2-4 hours, 15+ threats blocked"
    }
  },
  {
    id: 9,
    title: "nibble: Open Source Go Library",
    problem: "Bit-level binary protocols in Go require hand-written, error-prone parsers. Existing libraries are either too rigid or too slow for high-throughput packet pipelines (millions of packets per second).",
    solution: "Built `nibble`, a declarative bit-level parsing library that uses Go struct tags in the same pattern as encoding/json. Implemented a reflection-based engine with an aggressive type/field plan cache, pre-computed bit offsets, a sync.Pool of decode plans, inlined bit-readers, and a zero-allocation hot path so each Unmarshal call only touches the input buffer and the destination struct.",
    results: "Achieved 182 ns/op for Unmarshal (~11x improvement over the naive reflection version) and ~10x faster than go-bitfield, sustaining 5M packets/sec on a single core. Published a public benchmark report on GitHub Pages.",
    timeline: "2 months",
    role: "Author / Maintainer",
    teamSize: "Solo project",
    technologies: ["Go", "Reflection", "Binary Parsing", "Benchmarking", "sync.Pool", "GitHub Actions", "GitHub Pages"],
    flowchart: {
      problem: "Fast, declarative bit-level binary parsing in Go",
      stack: ["Go", "reflect", "sync.Pool", "Benchmarks"],
      implementation: ["Struct-tag driven API", "Cached reflection plans", "Inlined bit readers", "Zero-alloc hot path"],
      result: "182 ns/op, 5M pkt/s/core, 10x vs go-bitfield"
    }
  },
  {
    id: 5,
    title: "ElasticSearch Master Search Engine",
    problem: "Enterprise-wide data lookup was slow and inefficient. Users struggled with exact-match queries across multiple systems. Search taking 30+ seconds for complex queries.",
    solution: "Built a Django-based search API that queries ElasticSearch while a custom Django ingestion service (with Logstash) curates data from Atlassian tools, Splunk bucket, Apache Solr, and other sources. Implemented query normalization (tokenization + lemmatization), synonym expansion, and BM25-based relevance scoring for fast, accurate search.",
    results: "Achieved 50% faster lookup (30s → 15s average). Increased search accuracy by 40% through NLP. Indexed 10M+ records across 5 different systems with sub-second response time.",
    timeline: "6 months",
    role: "Lead Developer & ML Engineer",
    teamSize: "4 engineers",
    technologies: ["Python", "Django", "ElasticSearch", "Logstash", "ML", "NLP", "Atlassian", "Splunk", "Apache Solr", "Terraform"],
    flowchart: {
      problem: "Slow searches (30s+) with poor accuracy",
      stack: ["Django API", "ElasticSearch", "Logstash", "ML/NLP Models"],
      implementation: ["Django query API", "Logstash + Django ingestion", "Schema normalization + de-duplication", "BM25 ranking + synonym expansion", "Multi-system indexing"],
      result: "50% faster, 40% more accurate, <1s response"
    }
  },
  {
    id: 8,
    title: "K8s Time-Series Forecasting Platform",
    problem: "Need a Kubernetes-first forecasting system that can handle high QPS, keep PostgreSQL stable, and support repeatable training/evaluation with safe model promotion and rollback.",
    solution: "Built a K8s-native NeuralProphet platform: raw series stored in PostgreSQL with PgBouncer for pooling, Redis for cache, and a queue for heavy/bulk forecasting. Training runs as Jobs/CronJobs, evaluators gate quality, and a promoter flips an active model alias for instant rollback. The forecast API runs as a Deployment with HPA; heavy forecasting runs as workers scaled by KEDA. Models are versioned in a registry bucket and loaded once per pod for low-latency inference.",
    results: "Sustained high QPS with stable DB connections, predictable training releases, and instant rollback by switching active model alias.",
    timeline: "5 months",
    role: "Platform Engineer / MLOps",
    teamSize: "3 engineers",
    technologies: [
      "Kubernetes",
      "NeuralProphet",
      "PostgreSQL",
      "PgBouncer",
      "Redis",
      "NATS/RabbitMQ/Kafka",
      "KEDA",
      "HPA",
      "S3/MinIO",
      "Prometheus/Grafana",
      "Terraform"
    ],
    flowchart: {
      problem: "High-QPS forecasting with safe model lifecycle",
      stack: ["Kubernetes", "NeuralProphet", "PostgreSQL", "Redis", "KEDA"],
      implementation: ["Jobs/CronJobs for training", "FAISS-free forecasting via API/Workers", "Active alias for rollback", "Cache + PgBouncer for stability"],
      result: "Stable DB, scalable QPS, safe releases"
    }
  },
  {
    id: 3,
    title: "Data Backup & Restore Automation",
    problem: "Manual RDS backup and restore process was error-prone, time-consuming, and lacked proper schema versioning. No reliable disaster recovery mechanism in place.",
    solution: "Automated backup/restore framework using Java, Python, and Bash. Implemented Liquibase for structured database schema/version control and MongoBee (or equivalent migration tooling) for unstructured/document database change management. Added schema-aware validation paths for both SQL and NoSQL backups, then stored encrypted artifacts across AWS S3, Azure Blob, and Google Cloud Storage for multi-cloud resilience and seamless rollback.",
    results: "Reduced backup time from 4 hours to 30 minutes. Achieved RPO of 15 minutes and RTO of 1 hour. Successfully tested disaster recovery 12+ times with 100% success rate.",
    timeline: "4 months",
    role: "Senior DevOps Engineer",
    teamSize: "2 engineers",
    technologies: ["Java", "Python", "Bash", "AWS RDS", "MongoDB", "Liquibase", "MongoBee", "AWS S3", "Azure Blob", "Google Cloud Storage", "Terraform"],
    flowchart: {
      problem: "Unreliable manual backup with no DR strategy",
      stack: ["RDS + MongoDB", "Liquibase + MongoBee", "S3 + Azure Blob + GCS", "Python/Java"],
      implementation: ["Automated snapshot creation", "Structured(SQL) and unstructured(NoSQL) migration handling", "Dual-path validation framework", "Multi-cloud backup storage"],
      result: "RPO 15min, RTO 1hr, 100% DR success rate"
    }
  },
  {
    id: 10,
    title: "MCP Security Scanning Report",
    problem: "Personal research and development work to investigate the security posture of MCP (Model Context Protocol) servers as they proliferated rapidly. There was no consolidated view of vulnerability patterns — credential exposure, tool poisoning, weak transport security, and missing access control — and no large-scale audit existed.",
    solution: "As a research/dev exercise, built a scanning pipeline that crawled 500 MCP server packages into S3, then fanned out through Step Functions to multiple static scanners (AST, secret detection, tool-poisoning heuristics, transport security, access-control). Findings were fed into an LLM Jury evaluation pipeline on AWS SageMaker: multiple foundation models acted as workers proposing classifications, and a judge LLM aggregated their verdicts, with clustering/dedup before persisting to DynamoDB. Verdicts were then mapped to CWE IDs, scored for severity, and compiled into a remediation-focused report.",
    results: "Produced a comprehensive research report covering 500 MCP servers with CWE mappings, severity distributions, recurring vulnerability patterns, and remediation guidance — surfacing systemic issues across the ecosystem.",
    timeline: "3 months",
    role: "Security Research Engineer",
    teamSize: "2",
    technologies: ["AWS SageMaker", "LLM Jury", "Python", "Step Functions", "S3", "DynamoDB", "AST Analysis", "CWE", "Security Audit"],
    flowchart: {
      problem: "Audit 500 MCP servers for systemic security risks",
      stack: ["SageMaker", "LLM Jury", "Step Functions", "S3 + DynamoDB"],
      implementation: ["Crawl + fan-out scanning", "Multi-LLM worker classification", "Judge LLM aggregation + clustering", "CWE mapping + remediation"],
      result: "500 pkgs audited, CWE-mapped findings, full report"
    }
  },
  {
    id: 11,
    title: "Multi-Tenant Keycloak IAM Modernisation (KC15 → KC26)",
    problem: "A 30-tenant Keycloak SSO fleet (SAML, OIDC, Google federation) was stuck on Keycloak 15/Wildfly — end-of-life, Java 8-era, and carrying a custom SPI that the Wildfly-to-Quarkus rewrite would break. Migrations had to be provably lossless (secrets, mappers, users, password hashes) with zero login downtime per customer.",
    solution: "Built config-driven migration machinery: one YAML per customer feeding a boto3 deployer that provisions an ASG on ARM Graviton with a rootless Quarkus install, per-env HA/cache/timezone tuning, S3-driven login theming (CSS-only to preserve test-automation XPaths), and automated Splunk log forwarding. Ported the custom persona-aware SAML attribute-mapper SPI across the Wildfly→Quarkus API rewrite (Java 21) and embedded it in the pipeline so every instance self-installs it at build. Realm and user exports preserved password hashes, federated identity links, and roles; migrations were verified by md5 checksum diffs of client/IdP secrets and full mapper-config diffs between the KC15 and KC26 databases. KC26 breaking changes (RFC 9207 RP-initiated logout, legacy-adapter iss/session_state callbacks, IdP deprecations) were standardised via environment-templated config stamped at build time. Cutovers ran blue-green via an ALB preview target group with a TG swap and a 24-hour rollback window.",
    results: "Migrated the fleet with zero-downtime cutovers and checksum-verified realm parity — cutovers proven equivalent, not assumed. The cutover runbook reduced per-customer migration effort from days to hours.",
    timeline: "4 months",
    role: "Lead IAM / Platform Engineer",
    teamSize: "2 engineers",
    technologies: ["Keycloak 26", "Quarkus", "SAML 2.0", "OIDC", "OAuth2", "Custom Keycloak SPI", "Java 21", "AWS EC2 ASG", "ALB", "S3", "RDS PostgreSQL", "Graviton/ARM", "boto3", "Blue-Green Deployment", "Splunk"],
    flowchart: {
      problem: "Fleet-scale IAM migration across a major-version chasm",
      stack: ["Keycloak 26 / Quarkus", "Custom SPI (Java 21)", "AWS ASG + ALB", "Splunk"],
      implementation: ["YAML-per-tenant deployer", "SPI port + build-time self-install", "md5-verified realm parity", "Blue-green TG cutover + rollback"],
      result: "30 tenants, zero downtime, days → hours per cutover"
    }
  },
  {
    id: 12,
    title: "Cloud Data Lakehouse on AWS (Medallion Architecture)",
    problem: "Three heterogeneous sources — a PostgreSQL OLTP database, a payments REST API, and partner SFTP files — needed to become one queryable, trustworthy analytics platform with a zero-data-loss guarantee, PII protection, and a defensible cost model.",
    solution: "Architected a Bronze/Silver/Gold medallion lakehouse on S3: AWS DMS change-data-capture for the OLTP source, Lambda on an EventBridge schedule for the payments API, and AWS Transfer Family for partner SFTP. PySpark on EC2 handled Bronze→Silver cleansing behind a seven-check data quality layer (schema contract validation, window + MD5 deduplication, referential integrity, SHA-256 PII masking, range and consistency checks) — every rejected row quarantined with a reason code and run_id for full lineage and idempotent replay. AWS Glue modelled Silver→Gold star schemas with SCD Type 2 dimensions, catalogued in the Glue Data Catalog and served by Athena. Observability ran through Splunk, CloudWatch, and Kinesis Firehose with severity-tiered alerting; the whole stack deploys from modular Terraform in ~15 minutes.",
    results: "Processed ~500K orders, 505K payments, and 1.03M order items per run; quarantined 35,000+ rows with zero data lost; removed 10,000 duplicates; surfaced KPIs across $85M revenue and a 2.49% fraud rate at 1–2 second Athena query latency. Cut monthly pipeline spend 69% ($371 → $116) by comparing Glue, Fargate, and Athena CTAS strategies.",
    timeline: "3 months",
    role: "Data Platform Engineer / Architect",
    teamSize: "Solo project",
    technologies: ["AWS Glue", "PySpark", "Amazon S3", "Athena", "AWS DMS", "Lambda", "EventBridge", "Step Functions", "AWS Transfer Family", "Kinesis Firehose", "Parquet", "Glue Data Catalog", "Terraform", "PostgreSQL", "Splunk"],
    flowchart: {
      problem: "Three messy sources → one trustworthy lakehouse",
      stack: ["S3 Medallion", "DMS CDC", "PySpark + Glue", "Athena"],
      implementation: ["Three ingestion paths", "7-check DQ + quarantine w/ lineage", "Star schema + SCD Type 2", "Severity-tiered alerting + FinOps"],
      result: "2M+ rows/run, zero data loss, 69% cost cut"
    }
  },
  {
    id: 13,
    title: "AI Platform Cloud Portability (Vendor Lock-in Remediation)",
    problem: "A production AI product was locked to GCP managed services (Firestore, Pinecone, Neo4j, Cloud Run IAM), blocking on-prem and self-hosted delivery for compliance-sensitive enterprise customers. The system was live — evolution had to carry zero regression risk.",
    solution: "Applied a strangler-fig adapter-seam pattern: config-gated backend seams (DB_BACKEND, GRAPH_BACKEND, VECTOR_BACKEND) where the default preserves existing production behaviour byte-for-byte and a single environment variable flips to the in-house backend. Migrated three storage paradigms onto one self-hosted Postgres — Firestore → jsonb, Pinecone → pgvector, Neo4j → Apache AGE — with tenant-scoped schema design and shape-preserving round-trips. Designed a hybrid control-plane/data-plane split (SaaS auth control plane, customer-deployable data plane) and unified self-host delivery into a single docker-compose stack with a custom Postgres image bundling AGE and pgvector, healthchecks, dependency ordering, and env_file secrets. Every change ran the full test suite plus in-container end-to-end verification against real Postgres.",
    results: "Re-architected a GCP-locked SaaS product into a portable, self-hostable stack with zero regressions, cutting third-party managed-service dependencies and unlocking on-prem delivery for compliance-sensitive enterprise customers.",
    timeline: "3 months",
    role: "Platform Architect",
    teamSize: "Solo project",
    technologies: ["GCP", "Cloud Run", "Firestore", "Pinecone", "Neo4j", "PostgreSQL jsonb", "pgvector", "Apache AGE", "Docker", "docker-compose", "Firebase Auth", "Python"],
    flowchart: {
      problem: "GCP-locked AI product blocking on-prem delivery",
      stack: ["Config-gated seams", "Postgres jsonb + pgvector + AGE", "docker-compose", "Firebase Auth"],
      implementation: ["Strangler-fig adapters", "3 storage paradigms → 1 Postgres", "Control/data plane split", "E2E verification per change"],
      result: "Self-hostable stack, zero regressions"
    }
  },
  {
    id: 6,
    title: "Batch Service Pipeline",
    problem: "Customer data files from downstream systems required manual ingestion, validation, and loading. Process was error-prone with frequent data inconsistencies and no change tracking.",
    solution: "Engineered automated Bash, Python, Groovy and Go based batch pipelines. Implemented format validation, change comparison algorithms, and incremental loading into application database with rollback capability.",
    results: "Automated 100% of data ingestion workflows. Reduced data loading errors by 95%. Processing time decreased from 8 hours to 45 minutes. Successfully handled 50+ customer data feeds daily.",
    timeline: "3 months",
    role: "DevOps Automation Engineer",
    teamSize: "2 engineers",
    technologies: ["Bash", "Python", "Groovy", "SQL", "Cron", "Terraform"],
    flowchart: {
      problem: "Manual data ingestion, error-prone, no tracking",
      stack: ["Bash", "Python", "Groovy","Go", "Java", "Database"],
      implementation: ["Automated file ingestion", "Format validation", "Change detection", "Incremental loading"],
      result: "8hrs → 45min, 95% fewer errors, 50+ feeds/day"
    }
  }
];

export const skills = {
  cloud: ["AWS", "AWS S3", "GCP", "Azure", "EventBridge", "Step Functions", "Lambda", "S3/MinIO"],
  containers: ["Docker", "Amazon ECS", "Kubernetes", "EKS/GKE", "Helm", "KEDA", "HPA"],
  cicd: ["Jenkins", "GitLab CI", "Bamboo", "GitHub Actions"],
  iac: ["Terraform", "Ansible", "CloudFormation"],
  monitoring: ["CloudWatch", "AWS CloudWatch", "Splunk", "ScienceLogic", "Uptime Kuma", "Prometheus", "Grafana", "Prometheus/Grafana"],
  security: ["HashiCorp Vault", "CrowdStrike", "Carbon Black", "STIG Compliance", "Keycloak", "SAML 2.0 / OIDC", "IAM"],
  programming: ["Python", "Python Flask", "Django", "Java", "Bash", "NodeJS", "Groovy", "ReactJS", "SQL"],
  databases: ["ElasticSearch", "AWS RDS", "MongoDB", "PostgreSQL", "DynamoDB/Postgres", "Apache Solr"],
  other: [
    "GraphQL",
    "REST APIs",
    "NLP",
    "ML",
    "SNMP",
    "Liquibase",
    "Logstash",
    "NATS/RabbitMQ/Kafka",
    "Cron",
    "SNS/SQS",
    "Camelot/Tabula",
    "Canonical Landscape",
    "Redis"
  ],
  mlops: ["RAG", "FAISS", "SentenceTransformers", "Whisper", "LangChain", "LLMOps", "Vector Search", "pgvector"],
  data: ["AWS Glue", "PySpark", "Athena", "AWS DMS (CDC)", "Parquet", "Medallion Lakehouse", "SCD Type 2", "Apache AGE"],
  forecasting: ["NeuralProphet", "Time-Series"]
};

export const education = [
  {
    id: 1,
    degree: "Master of Data Science",
    institution: "Monash University",
    period: "Feb 2022 - Apr 2023",
    location: "Melbourne, AU",
    details: "Winter Scholarship - Neural Prophet (Dr. Christoph Bergmeir)"
  },
  {
    id: 2,
    degree: "B.E., Computer Science",
    institution: "Visvesvaraya Technological University",
    period: "Sep 2012 - Sep 2016",
    location: "Bangalore, IN",
    details: "Best Project & Paper of the Batch 2016"
  }
];

export const awards = [
  "Visuothon 2022 - 2nd Place - Monash Data Science Society",
  "Google Student Ambassador",
  "GDG Bangalore Organiser (2017-2019)",
  "NCCT-16 Research Paper - Automated Dubbing System"
];
